import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { createClient } from "@supabase/supabase-js";

// -----------------
// Tipagens
// -----------------
interface User {
  id: string;
  cpf: string;
  nome: string;
  contato: string;
  desafio?: string;
  isAdmin: boolean;
}

type Usuario = {
  id: string;
  cpf: string;
  nome: string;
  contato: string;
  desafio?: string;
};

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (cpf: string) => Promise<boolean>;
  adminLogin: (cpf: string, password: string) => Promise<boolean>;
  logout: () => void;
}

// -----------------
// Supabase Client
// -----------------
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL!,
  import.meta.env.VITE_SUPABASE_ANON_KEY!
);

// -----------------
// Contexto
// -----------------
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // -----------------
  // Carrega sessão salva
  // -----------------
  useEffect(() => {
    const savedUser = localStorage.getItem("user_session");
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        localStorage.removeItem("user_session");
      }
    }
    setIsLoading(false);
  }, []);

  // -----------------
  // Buscar usuário por CPF
  // -----------------
  const buscarUsuarioPorCPF = async (cpf: string): Promise<Usuario | null> => {
    try {
      const { data, error } = await supabase
        .from("usuarios")
        .select("*")
        .eq("cpf", cpf)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("❌ Erro ao buscar CPF no Supabase:", error);
        return null;
      }

      if (data) {
        console.log("✅ Usuário encontrado:", data);
        return data as Usuario;
      }

      console.log("⚠️ CPF não encontrado no Supabase.");
      return null;
    } catch (err) {
      console.error("❌ Erro ao validar CPF:", err);
      return null;
    }
  };

  // -----------------
  // Login por CPF
  // -----------------
  const login = async (cpf: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const usuario = await buscarUsuarioPorCPF(cpf);

      if (usuario) {
        const userData: User = {
          id: usuario.id,
          cpf: usuario.cpf,
          nome: usuario.nome,
          contato: usuario.contato,
          desafio: usuario.desafio,
          isAdmin: false,
        };

        setUser(userData);
        localStorage.setItem("user_session", JSON.stringify(userData));
        return true;
      }

      return false;
    } catch (error) {
      console.error("❌ Erro no login:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // -----------------
  // Login administrativo (temporário: fixo)
  // -----------------
  const adminLogin = async (
    cpf: string,
    password: string
  ): Promise<boolean> => {
    setIsLoading(true);
    try {
      // Busca usuário pelo CPF
      const { data: usuario, error } = await supabase
        .from("usuarios")
        .select("id, cpf, nome, contato, tipo, senha")
        .eq("cpf", cpf)
        .maybeSingle();

      if (error || !usuario) {
        console.error("❌ CPF não encontrado ou erro na busca:", error);
        return false;
      }

      // Só prossegue se for admin
      if ((usuario.tipo || "").toLowerCase() !== "admin") {
        console.warn("🚫 Usuário não é administrador");
        return false;
      }

      // Verifica senha (texto puro)
      if (usuario.senha !== password) {
        console.warn("❌ Senha incorreta");
        return false;
      }

      // Define sessão no contexto
      const adminData: User = {
        id: usuario.id,
        cpf: usuario.cpf,
        nome: usuario.nome ?? "Administrador",
        contato: usuario.contato ?? "",
        isAdmin: true,
      };

      setUser(adminData);
      localStorage.setItem("user_session", JSON.stringify(adminData));
      return true;
    } catch (error) {
      console.error("❌ Erro no login admin:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // -----------------
  // Logout
  // -----------------
  const logout = () => {
    setUser(null);
    localStorage.removeItem("user_session");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        adminLogin,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// -----------------
// Hook para uso do contexto
// -----------------
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
